import { useEffect, useMemo, useState } from "react";
import { apiCall, API, getAuthHeaders } from "../api";
import { Btn } from "./common/FormElements";

// The file the HR KPI Center imports. A different file from "HR Export (Monthly
// JSON)" next door, which is the month's records and archive — that one stays
// exactly as it is.
export default function HRKpiExport() {
  const [options, setOptions] = useState(null);
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState("");
  const [picked, setPicked] = useState([]);       // [] = every unit
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    apiCall("GET", "/hr-kpi-export/options")
      .then(data => {
        setOptions(data);
        setFy(data.current_fy);
      })
      .catch(() => setError("Could not load the export options."));
  }, []);

  const months = useMemo(
    () => options?.financial_years?.find(f => f.fy === fy)?.months || [],
    [options, fy]
  );

  // Default to the newest month that has actually started — exporting a month
  // that has not begun would produce an empty file.
  useEffect(() => {
    if (!months.length) return;
    const selectable = months.filter(m => m.selectable);
    setMonth(selectable.length ? selectable[selectable.length - 1].month : "");
  }, [months]);

  const chosen = months.find(m => m.month === month);

  async function download() {
    if (!fy || !month) return;
    setBusy(true);
    setError("");
    try {
      const qs = picked.length ? `?unit=${encodeURIComponent(picked.join(","))}` : "";
      const res = await fetch(`${API}/hr-kpi-export/${fy}/${month}${qs}`, {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) {
        let detail = "";
        try { detail = (await res.json()).detail || ""; } catch { /* non-JSON body */ }
        throw new Error(detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const name = (res.headers.get("Content-Disposition") || "")
        .match(/filename="([^"]+)"/)?.[1] || `AAMPL-HR-KPI-${fy}-${month}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Export failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4" data-testid="hr-kpi-export">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-gray-700 space-y-2">
        <h3 className="font-bold text-gray-800">Export for HR KPI Center</h3>
        <p>
          One file per financial-year month, in the structure the HR KPI Center imports.
          It carries <code>source</code>, <code>fy</code>, <code>month</code>,{" "}
          <code>period</code> and <code>units</code> at the top level, with one{" "}
          <code>data</code> block per plant.
        </p>
        <ul className="list-disc pl-5 space-y-1 text-xs text-gray-600">
          <li>
            The figures are the app's own — the same Reports engine behind the dashboard
            and the <code>/api/kpi-export</code> feed. Nothing is recalculated for HR,
            so the three can never disagree.
          </li>
          <li>
            A month still in progress is exported to today and marked{" "}
            <code>month_complete: false</code>. A finished month is marked true.
          </li>
          <li>
            A unit that filed nothing reports <code>data_available: false</code> with a
            reason, and its figures travel as null — never as zero.
          </li>
          <li>
            The HR KPI Center skips any unit code it does not itself claim and warns
            about it on every import. Tick only the plants HR scores — typically{" "}
            <strong>Unit 1</strong> and <strong>Unit 3</strong> — to send a file with no
            unknown codes in it.
          </li>
          <li>
            This is <strong>not</strong> the same file as “HR Export (Monthly JSON)”,
            which remains the month's records and archive.
          </li>
        </ul>
        {options?.today_ist && (
          <p className="text-xs text-gray-500">Today in IST: {options.today_ist}</p>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-start">
          <div>
            <label className="text-xs text-gray-500 font-medium">Financial Year</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white mt-0.5"
              value={fy} onChange={e => setFy(e.target.value)} data-testid="hr-kpi-fy"
            >
              {(options?.financial_years || []).map(f => (
                <option key={f.fy} value={f.fy}>{f.fy}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium">Month</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white mt-0.5"
              value={month} onChange={e => setMonth(e.target.value)} data-testid="hr-kpi-month"
            >
              {months.map(m => (
                <option key={m.month} value={m.month} disabled={!m.selectable}>
                  {m.month}{!m.selectable ? " — not started" : m.month_complete ? "" : " — in progress"}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-gray-500 font-medium">
              Units to include{" "}
              <span className="text-gray-400">(none ticked = all units)</span>
            </label>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1" data-testid="hr-kpi-units">
              {(options?.units || []).map(u => (
                <label key={u.id} className="flex items-center gap-1.5 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={picked.includes(u.id)}
                    onChange={e => setPicked(p =>
                      e.target.checked ? [...p, u.id] : p.filter(x => x !== u.id))}
                    data-testid={`hr-kpi-unit-${u.id}`}
                  />
                  {u.short_name || u.name || u.id}
                  <span className="text-xs text-gray-400 font-mono">{u.id}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-3">
          <Btn onClick={download} disabled={busy || !fy || !month} testId="hr-kpi-download">
            {busy ? "Preparing..." : "Download HR KPI JSON"}
          </Btn>
        </div>

        {chosen && (
          <p className="text-xs text-gray-500 mt-3" data-testid="hr-kpi-period">
            Period covered: {chosen.period_start} &rarr; {chosen.period_end}
            {chosen.month_complete
              ? " (month complete)"
              : chosen.selectable
                ? ` (in progress — counted to ${options?.today_ist})`
                : " (not started)"}
          </p>
        )}
        {error && (
          <p className="text-xs text-red-600 mt-2" data-testid="hr-kpi-error">{error}</p>
        )}
      </div>
    </div>
  );
}
